import React, { useState, useEffect } from 'react';
import { 
  connectWorkspace, 
  getCachedToken, 
  hasActiveWorkspaceConnection,
  createGoogleDoc,
  createGoogleForm,
  createGoogleSheet,
  getGoogleTasks,
  createGoogleTask,
  toggleGoogleTaskStatus,
  getGoogleContacts,
  sendGmailEmail,
  listDriveFiles,
  uploadFileToDrive,
  getCalendarEvents,
  createCalendarEvent,
  GoogleTask,
  GoogleContact,
  DriveFile,
  CalendarEvent,
  setCachedToken
} from '../services/googleAuth';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { useAuth } from '../AuthContext';
import { 
  Cloud,
  FileText, 
  FileSpreadsheet, 
  CheckSquare, 
  Users, 
  Mail, 
  Folder, 
  Calendar as CalendarIcon, 
  Sparkles,
  RefreshCw,
  Plus,
  Send,
  ExternalLink,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileCode,
  Check,
  Search,
  ChevronRight,
  Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export const WorkspaceView: React.FC = () => {
  const { savedList, addToast } = useSavedSolicitations();
  const { user } = useAuth();
  
  const [isConnected, setIsConnected] = useState(hasActiveWorkspaceConnection());
  const [isConnecting, setIsConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState<'docs' | 'sheets' | 'tasks' | 'gmail' | 'calendar' | 'drive'>('docs');

  // API Data States
  const [loading, setLoading] = useState(false);
  
  // Drive States
  const [driveFiles, setDriveFiles] = useState<DriveFile[]>([]);
  const [searchDriveQuery, setSearchDriveQuery] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadContent, setUploadContent] = useState('');
  const [selectedDriveFile, setSelectedDriveFile] = useState<DriveFile | null>(null);

  // Docs States
  const [docTitle, setDocTitle] = useState('');
  const [docContent, setDocContent] = useState('');
  const [selectedSolicitationForDoc, setSelectedSolicitationForDoc] = useState<string>('');

  // Sheets & Forms States
  const [sheetTitle, setSheetTitle] = useState('ProcureFlow Tracked Bids Export');
  const [formTitle, setFormTitle] = useState('Vendor Pre-Qualification Questionnaire');
  const [formDescription, setFormDescription] = useState('This form gathers required certification and capability responses from subcontracting partners.');

  // Tasks States
  const [tasks, setTasks] = useState<GoogleTask[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskNotes, setNewTaskNotes] = useState('');
  const [selectedSolicitationForTask, setSelectedSolicitationForTask] = useState<string>('');

  // Contacts & Gmail States
  const [contacts, setContacts] = useState<GoogleContact[]>([]);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedContact, setSelectedContact] = useState<string>('');

  // Calendar States
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [eventSummary, setEventSummary] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventDuration, setEventDuration] = useState('60'); // Minutes
  const [selectedSolicitationForCal, setSelectedSolicitationForCal] = useState<string>('');
  const [tabErrors, setTabErrors] = useState<Record<string, string>>({});

  // Refresh active connection state on mount
  useEffect(() => {
    setIsConnected(hasActiveWorkspaceConnection());
  }, []);

  // Fetch tab-specific data when tab changes or connection is established
  useEffect(() => {
    if (isConnected) {
      fetchTabData();
    }
  }, [isConnected, activeTab]);

  const fetchTabData = async () => {
    if (!hasActiveWorkspaceConnection()) {
      setIsConnected(false);
      return;
    }

    setLoading(true);
    setTabErrors(prev => ({ ...prev, [activeTab]: '' }));
    try {
      if (activeTab === 'drive') {
        const files = await listDriveFiles(searchDriveQuery ? `name contains '${searchDriveQuery}'` : undefined);
        setDriveFiles(files || []);
      } else if (activeTab === 'tasks') {
        const items = await getGoogleTasks();
        setTasks(items || []);
      } else if (activeTab === 'gmail') {
        const people = await getGoogleContacts();
        setContacts(people || []);
      } else if (activeTab === 'calendar') {
        const events = await getCalendarEvents();
        setCalendarEvents(events || []);
      }
    } catch (error: any) {
      console.warn(`[Workspace ${activeTab}] Info:`, error?.message || error);
      const msg = error.message || 'Could not fetch latest Google Workspace data.';
      if (msg.includes('expired') || msg.includes('401')) {
        setIsConnected(false);
      }
      setTabErrors(prev => ({ ...prev, [activeTab]: msg }));
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const token = await connectWorkspace();
      if (token) {
        setIsConnected(true);
        if (addToast) {
          addToast({
            title: 'Google Workspace Connected',
            message: 'Successfully authenticated! Your Google Docs, Sheets, Drive, Tasks, Gmail, Contacts, Calendar, and Picker are ready.',
            type: 'success'
          });
        }
      }
    } catch (error: any) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request' ||
        (typeof error?.message === 'string' && error.message.includes('popup-closed-by-user'))
      ) {
        // User closed or dismissed the popup window, gracefully reset state without error
        return;
      }
      console.error('Connection error:', error);
      if (addToast) {
        addToast({
          title: 'Connection Failed',
          message: error.message || 'Could not complete OAuth flow. Make sure you accepted the requested permissions.',
          type: 'danger'
        });
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setCachedToken(null);
    setIsConnected(false);
    if (addToast) {
      addToast({
        title: 'Workspace Disconnected',
        message: 'Your Google Workspace session token was cleared from memory.',
        type: 'info'
      });
    }
  };

  // --- Docs Operations ---
  const handleCreateDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docTitle.trim()) return;

    setLoading(true);
    try {
      let finalContent = docContent;
      if (selectedSolicitationForDoc) {
        const sol = savedList.find(s => (s.id || s.solicitationId) === selectedSolicitationForDoc);
        if (sol) {
          finalContent = `SOLICITATION EXPORT: ${sol.title}\n` +
            `========================================\n` +
            `ID: ${sol.solicitationId || 'N/A'}\n` +
            `Agency: ${sol.agency || 'N/A'}\n` +
            `Due Date: ${sol.dueDate || 'N/A'}\n` +
            `Status: Tracked\n` +
            `State: ${sol.state || 'N/A'}\n\n` +
            `----------------------------------------\n` +
            `Bid Proposal Notes:\n` +
            (docContent || 'Drafting and compliance response under preparation.');
        }
      }

      const doc = await createGoogleDoc(docTitle, finalContent);
      if (addToast) {
        addToast({
          title: 'Google Doc Created',
          message: `"${docTitle}" was successfully saved to your Google Docs.`,
          type: 'success'
        });
      }
      setDocTitle('');
      setDocContent('');
      setSelectedSolicitationForDoc('');
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Create Doc',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Sheets & Forms Operations ---
  const handleExportToSheet = async () => {
    if (savedList.length === 0) {
      if (addToast) {
        addToast({
          title: 'No Data to Export',
          message: 'Save or track at least one solicitation before exporting.',
          type: 'warning'
        });
      }
      return;
    }

    setLoading(true);
    try {
      const headers = ['Solicitation ID', 'Title', 'Agency', 'Due Date', 'State', 'Status'];
      const rows = savedList.map(sol => [
        sol.solicitationId || '',
        sol.title || '',
        sol.agency || '',
        sol.dueDate || '',
        sol.state || '',
        'Tracked'
      ]);

      const sheet = await createGoogleSheet(sheetTitle, headers, rows);
      if (addToast) {
        addToast({
          title: 'Google Sheet Created',
          message: `Exported ${savedList.length} solicitation(s) to "${sheetTitle}".`,
          type: 'success'
        });
      }
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Export Failed',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    setLoading(true);
    try {
      const questions = [
        { title: 'Subcontractor Business Name', type: 'TEXT' as const },
        { title: 'Primary Contact Email Address', type: 'TEXT' as const },
        { title: 'Do you hold active MBE/WBE/DBE Certification?', type: 'CHOICE' as const, options: ['Yes - MBE', 'Yes - WBE', 'Yes - DBE', 'No'] },
        { title: 'Years of Experience in Public Sector Contracts', type: 'TEXT' as const },
        { title: 'Are you available to meet all compliance guidelines stated in this RFQ?', type: 'CHOICE' as const, options: ['Yes', 'No', 'Need Clarification'] }
      ];

      await createGoogleForm(formTitle, formDescription, questions);
      if (addToast) {
        addToast({
          title: 'Google Form Created',
          message: `"${formTitle}" is now active and ready for subcontract responses.`,
          type: 'success'
        });
      }
      setFormTitle('');
      setFormDescription('');
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Create Form',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Tasks Operations ---
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    setLoading(true);
    try {
      let finalNotes = newTaskNotes;
      let finalDue: string | undefined = undefined;

      if (selectedSolicitationForTask) {
        const sol = savedList.find(s => (s.id || s.solicitationId) === selectedSolicitationForTask);
        if (sol) {
          finalNotes = `Procurement Task for: ${sol.title}\n` +
            `Agency: ${sol.agency || 'N/A'}\n\n` +
            (newTaskNotes || 'Complete compliant response documentation.');
          
          if (sol.dueDate) {
            // Google Tasks expects ISO 3339 formatted timestamps (e.g., 2026-06-27T00:00:00Z)
            try {
              const d = new Date(sol.dueDate);
              if (!isNaN(d.getTime())) {
                finalDue = d.toISOString();
              }
            } catch (_) {}
          }
        }
      }

      await createGoogleTask(newTaskTitle, finalNotes, finalDue);
      if (addToast) {
        addToast({
          title: 'Google Task Created',
          message: `"${newTaskTitle}" added directly to your Google Tasks.`,
          type: 'success'
        });
      }
      setNewTaskTitle('');
      setNewTaskNotes('');
      setSelectedSolicitationForTask('');
      fetchTabData(); // Reload tasks
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Add Task',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    setLoading(true);
    try {
      await toggleGoogleTaskStatus(taskId, currentStatus);
      if (addToast) {
        addToast({
          title: 'Task Updated',
          message: 'Task status synchronized with Google Tasks.',
          type: 'success'
        });
      }
      fetchTabData(); // Reload tasks
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Update Task',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Gmail & Contacts Operations ---
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailTo.trim() || !emailSubject.trim() || !emailBody.trim()) return;

    // Explicit confirmation dialouge as per Workspace security mandates
    const confirmed = window.confirm(`Are you sure you want to send this email to ${emailTo} on behalf of your Google account?`);
    if (!confirmed) return;

    setLoading(true);
    try {
      await sendGmailEmail(emailTo, emailSubject, emailBody);
      if (addToast) {
        addToast({
          title: 'Email Sent via Gmail',
          message: `Message to ${emailTo} dispatched successfully.`,
          type: 'success'
        });
      }
      setEmailSubject('');
      setEmailBody('');
      setEmailTo('');
      setSelectedContact('');
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Send Email',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = (contactResourceName: string) => {
    const contact = contacts.find(c => c.resourceName === contactResourceName);
    if (contact) {
      setSelectedContact(contactResourceName);
      setEmailTo(contact.email);
      // Pre-fill subject if empty
      if (!emailSubject) {
        setEmailSubject('Collaboration on Procurement solicitation response');
      }
    }
  };

  // --- Calendar Operations ---
  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventSummary.trim() || !eventDate) return;

    setLoading(true);
    try {
      const start = new Date(eventDate);
      const end = new Date(start.getTime() + parseInt(eventDuration) * 60 * 1000);

      let finalDesc = eventDescription;
      if (selectedSolicitationForCal) {
        const sol = savedList.find(s => (s.id || s.solicitationId) === selectedSolicitationForCal);
        if (sol) {
          finalDesc = `Procurement Deadline Sync for: ${sol.title}\n` +
            `Agency: ${sol.agency || 'N/A'}\n` +
            `Due Date: ${sol.dueDate || 'N/A'}\n\n` +
            (eventDescription || 'Action Required: Submit final compliance package.');
        }
      }

      await createCalendarEvent({
        summary: eventSummary,
        description: finalDesc,
        start: start.toISOString(),
        end: end.toISOString()
      });

      if (addToast) {
        addToast({
          title: 'Calendar Event Created',
          message: `"${eventSummary}" added successfully to your Google Calendar.`,
          type: 'success'
        });
      }
      setEventSummary('');
      setEventDescription('');
      setEventDate('');
      setSelectedSolicitationForCal('');
      fetchTabData(); // Reload events
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Failed to Schedule Event',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Drive & Picker Operations ---
  const handleUploadFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadName.trim() || !uploadContent.trim()) return;

    setLoading(true);
    try {
      await uploadFileToDrive(
        uploadName.endsWith('.txt') ? uploadName : `${uploadName}.txt`,
        'text/plain',
        uploadContent
      );
      if (addToast) {
        addToast({
          title: 'File Uploaded to Drive',
          message: `"${uploadName}" successfully saved to Google Drive.`,
          type: 'success'
        });
      }
      setUploadName('');
      setUploadContent('');
      fetchTabData(); // Refresh files list
    } catch (error: any) {
      if (addToast) {
        addToast({
          title: 'Upload Failed',
          message: error.message,
          type: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Custom solicitation pre-fill handler
  const handlePreFillDocFromSolicitation = (id: string) => {
    const sol = savedList.find(s => (s.id || s.solicitationId) === id);
    if (sol) {
      setDocTitle(`Draft Response - ${sol.title.slice(0, 40)}`);
      setDocContent(
        `DRAFT PROPOSAL FOR COMPLIANCE\n` +
        `----------------------------------------\n` +
        `We are pleased to submit our response to Solicitation ID: ${sol.solicitationId || 'N/A'}.\n\n` +
        `Compliance Checklist Status: Complete\n` +
        `Subcontracting Goal: Compliant\n` +
        `Cost Estimate: Standard State Tariff Schedule\n\n` +
        `Ready for review and submission.`
      );
    }
  };

  const handlePreFillTaskFromSolicitation = (id: string) => {
    const sol = savedList.find(s => (s.id || s.solicitationId) === id);
    if (sol) {
      setNewTaskTitle(`Submit response for ${sol.solicitationId || 'RFP'}`);
      setNewTaskNotes(`Review instructions for agency ${sol.agency || 'Public Procurement'}. Ensure DBE metrics are uploaded.`);
    }
  };

  const handlePreFillCalFromSolicitation = (id: string) => {
    const sol = savedList.find(s => (s.id || s.solicitationId) === id);
    if (sol) {
      setEventSummary(`DEADLINE: RFP ${sol.solicitationId || ''} Response Due`);
      setEventDescription(`Submit complete package for solicitation: ${sol.title}`);
      if (sol.dueDate) {
        try {
          const d = new Date(sol.dueDate);
          if (!isNaN(d.getTime())) {
            // Set event 1 hour before due date
            d.setHours(d.getHours() - 1);
            setEventDate(d.toISOString().slice(0, 16));
          }
        } catch (_) {}
      }
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm relative overflow-hidden">
        <div className="space-y-1 relative z-10">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
            <Cloud className="w-6.5 h-6.5 text-indigo-600 animate-pulse" />
            Google Workspace Integration Hub
          </h1>
          <p className="text-gray-500 text-sm max-w-2xl">
            Seamlessly connect your business workspace. Export bid opportunities, schedule submission deadlines, compose outreach emails, assign tasks, and store proposal docs directly in your Google account.
          </p>
        </div>
        
        <div className="relative z-10 shrink-0">
          {isConnected ? (
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                Workspace Connected
              </span>
              <button
                onClick={handleDisconnect}
                className="px-3.5 py-1.5 text-xs font-medium border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="gsi-material-button inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold text-sm rounded-lg shadow-sm transition-all cursor-pointer disabled:opacity-55"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  Connecting Google...
                </>
              ) : (
                <div className="flex items-center gap-2.5">
                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  </svg>
                  <span>Connect Google Workspace</span>
                </div>
              )}
            </button>
          )}
        </div>
        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-50/30 rounded-full blur-3xl" />
      </div>

      {/* Connection Guards */}
      {!isConnected ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-6 text-center shadow-xs">
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto text-indigo-600">
                  <Cloud className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Authorize Workspace Integration</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  ProcureFlow needs secure access to communicate with your Google account. This lets you generate live compliance documents, spreadsheets, custom task lists, and calendar sync logs automatically.
                </p>
                <div className="pt-2">
                  <button
                    onClick={handleConnect}
                    disabled={isConnecting}
                    className="px-6 py-2.5 bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm rounded-xl transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2 mx-auto cursor-pointer"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Activate Workspace
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Feature List */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
              <h4 className="font-bold text-gray-900 text-sm tracking-tight flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" /> Enabled Workspace Integrations
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                {[
                  { name: 'Google Docs', desc: 'Create live proposal response drafts.', color: 'text-blue-600' },
                  { name: 'Google Forms', desc: 'Acquire pre-qualification responses.', color: 'text-purple-600' },
                  { name: 'Google Sheets', desc: 'Tabulate opportunities for quick pricing.', color: 'text-emerald-600' },
                  { name: 'Google Tasks', desc: 'Establish tracking checklists for submissions.', color: 'text-indigo-600' },
                  { name: 'Google Contacts', desc: 'Access joint partners for fast sharing.', color: 'text-cyan-600' },
                  { name: 'Gmail API', desc: 'Send bid packages directly from your account.', color: 'text-red-500' },
                  { name: 'Google Drive', desc: 'Store procurement templates on cloud.', color: 'text-amber-500' },
                  { name: 'Google Calendar', desc: 'Synchronize submission dates cleanly.', color: 'text-blue-500' },
                  { name: 'Google Picker', desc: 'Import attachments into response drafts.', color: 'text-teal-600' }
                ].map((s, idx) => (
                  <div key={idx} className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 flex gap-2.5 items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-1.5" />
                    <div>
                      <p className={`font-bold ${s.color}`}>{s.name}</p>
                      <p className="text-gray-400 mt-0.5 leading-normal">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-amber-50/50 border border-amber-200 rounded-2xl p-6 space-y-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="font-bold text-amber-900 text-sm">Security & Scopes</h4>
                  <p className="text-xs text-amber-800 leading-normal">
                    This integration strictly uses Google OAuth2. Token cache is stored in-memory during active sessions. No access credentials or emails are permanently written to external servers.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50/30 border border-indigo-100 rounded-2xl p-6 space-y-3">
              <h4 className="font-bold text-indigo-950 text-sm">Workspace Scope Checklist</h4>
              <ul className="space-y-2 text-xs text-indigo-900">
                <li className="flex items-center gap-2">✓ documents.readonly & write</li>
                <li className="flex items-center gap-2">✓ drive.file & drive.readonly</li>
                <li className="flex items-center gap-2">✓ forms.body & response</li>
                <li className="flex items-center gap-2">✓ spreadsheets & sheets.readonly</li>
                <li className="flex items-center gap-2">✓ gmail.send & mail.google.com</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Navigation panel */}
          <div className="lg:col-span-3 space-y-3">
            {[
              { id: 'docs', label: 'Docs, Sheets & Forms', desc: 'Google Document tools', icon: FileText, color: 'text-indigo-600 bg-indigo-50' },
              { id: 'drive', label: 'Drive Explorer & Picker', desc: 'Manage bid attachments', icon: Folder, color: 'text-amber-600 bg-amber-50' },
              { id: 'tasks', label: 'Tasks & Action Items', desc: 'Procurement checklists', icon: CheckSquare, color: 'text-emerald-600 bg-emerald-50' },
              { id: 'gmail', label: 'Gmail & Contacts Link', desc: 'Dispatch RFP updates', icon: Mail, color: 'text-red-500 bg-rose-50' },
              { id: 'calendar', label: 'Calendar Event Sync', desc: 'Deadline countdown logs', icon: CalendarIcon, color: 'text-blue-600 bg-blue-50' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as any)}
                className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer flex gap-3.5 items-start ${
                  activeTab === item.id 
                    ? 'border-indigo-600 bg-indigo-50/40 shadow-sm' 
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className={`p-2.5 rounded-lg shrink-0 ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-gray-900 tracking-tight">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Active Panel View */}
          <div className="lg:col-span-9 bg-white rounded-2xl border border-gray-200 p-6 shadow-xs min-h-[500px] flex flex-col justify-between relative overflow-hidden">
            {loading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-xs z-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-2.5">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  <p className="text-xs font-semibold text-gray-600 animate-pulse">Syncing with Google Workspace...</p>
                </div>
              </div>
            )}

            {tabErrors[activeTab] ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 max-w-lg mx-auto space-y-4 my-auto">
                <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <div className="space-y-1.5">
                  <h4 className="font-bold text-gray-900 text-sm">Workspace Integration Issue</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    We encountered an issue interacting with your Google {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} account:
                  </p>
                  <p className="text-xs font-mono bg-rose-50 text-rose-700 p-2.5 rounded-lg border border-rose-100 mt-2 text-left break-all">
                    {tabErrors[activeTab]}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-4 w-full">
                  <button
                    onClick={fetchTabData}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors shadow-xs"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry Syncing Tab
                  </button>
                  <button
                    onClick={async () => {
                      setCachedToken('');
                      setIsConnected(false);
                      setTabErrors({});
                      if (addToast) {
                        addToast({
                          title: 'Session Cleared',
                          message: 'Google Workspace credentials cleared. Please click connect to authenticate again.',
                          type: 'info'
                        });
                      }
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                  >
                    Reconnect Google Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
              {/* === TAB CONTENT: DOCS & SHEETS & FORMS === */}
              {activeTab === 'docs' && (
                <div className="space-y-6 animate-fade-in">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-600" /> Documents, Spreadsheets & Intake Forms
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Author dynamic Google Docs for solicitation responses, export tracked bids to Google Sheets, or create pre-qualification Forms.
                    </p>
                  </div>

                  {/* Section Divider: Docs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <form onSubmit={handleCreateDoc} className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-xl p-5">
                      <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" /> Google Docs Response Drafting
                      </h4>
                      
                      <div className="space-y-3.5">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Link tracked Solicitation (Optional)</label>
                          <select
                            value={selectedSolicitationForDoc}
                            onChange={(e) => {
                              setSelectedSolicitationForDoc(e.target.value);
                              handlePreFillDocFromSolicitation(e.target.value);
                            }}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          >
                            <option value="">-- Select solicitation to pre-fill content --</option>
                            {savedList.map((sol) => (
                              <option key={sol.id || sol.solicitationId} value={sol.id || sol.solicitationId}>
                                [{sol.solicitationId}] {sol.title.slice(0, 45)}...
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Doc Title</label>
                          <input
                            type="text"
                            required
                            placeholder="Enter document title..."
                            value={docTitle}
                            onChange={(e) => setDocTitle(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Initial Draft Content</label>
                          <textarea
                            rows={4}
                            placeholder="Draft your proposal introduction or response content here..."
                            value={docContent}
                            onChange={(e) => setDocContent(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-mono"
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Export to Google Doc
                        </button>
                      </div>
                    </form>

                    <div className="space-y-6">
                      {/* Sheets Card */}
                      <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 space-y-4">
                        <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Export Tracked Bids to Sheets
                        </h4>
                        <p className="text-xs text-gray-500 leading-normal">
                          Instantly construct a detailed Google Spreadsheet containing all currently saved and tracked solicitations, due dates, agency issuers, and status records.
                        </p>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Spreadsheet Title</label>
                          <input
                            type="text"
                            value={sheetTitle}
                            onChange={(e) => setSheetTitle(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>
                        <button
                          onClick={handleExportToSheet}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <FileSpreadsheet className="w-4 h-4" /> Generate Google Sheet ({savedList.length} Rows)
                        </button>
                      </div>

                      {/* Forms Card */}
                      <form onSubmit={handleCreateForm} className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 space-y-4">
                        <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-purple-600" /> Create Intake Form (Subcontractors)
                        </h4>
                        
                        <div className="space-y-3">
                          <div>
                            <input
                              type="text"
                              required
                              placeholder="Form Title..."
                              value={formTitle}
                              onChange={(e) => setFormTitle(e.target.value)}
                              className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                            />
                          </div>
                          <div>
                            <textarea
                              rows={2}
                              placeholder="Form Description..."
                              value={formDescription}
                              onChange={(e) => setFormDescription(e.target.value)}
                              className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Create Google Form
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* === TAB CONTENT: DRIVE EXPLORER & PICKER === */}
              {activeTab === 'drive' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Folder className="w-5 h-5 text-amber-600" /> Google Drive Explorer & Picker
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Pick bid solicitations, view file details, and upload new compliance templates directly from within ProcureFlow.
                      </p>
                    </div>
                    
                    <button
                      onClick={fetchTabData}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Refresh Drive
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* File Picker list */}
                    <div className="md:col-span-7 bg-gray-50/50 border border-gray-100 rounded-xl p-4 space-y-4 flex flex-col h-[400px]">
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 shrink-0">
                        <Search className="w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search files on Drive..."
                          value={searchDriveQuery}
                          onChange={(e) => setSearchDriveQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && fetchTabData()}
                          className="w-full text-xs bg-transparent border-none outline-none text-gray-700"
                        />
                      </div>

                      <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {driveFiles.length === 0 ? (
                          <div className="text-center py-20 text-gray-400 text-xs">
                            No files found in Google Drive matching criteria.
                          </div>
                        ) : (
                          driveFiles.map((file) => (
                            <button
                              key={file.id}
                              onClick={() => setSelectedDriveFile(file)}
                              className={`w-full text-left p-3 rounded-lg border text-xs transition-all flex justify-between items-center cursor-pointer ${
                                selectedDriveFile?.id === file.id
                                  ? 'border-indigo-600 bg-indigo-50/50'
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <FileText className={`w-4 h-4 shrink-0 ${(file.mimeType || '').includes('spreadsheet') ? 'text-emerald-600' : 'text-blue-600'}`} />
                                <div className="truncate">
                                  <p className="font-semibold text-gray-900 truncate">{file.name}</p>
                                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                                    Modified: {file.modifiedTime ? new Date(file.modifiedTime).toLocaleDateString() : 'N/A'}
                                  </p>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Picker/File Preview & Upload panel */}
                    <div className="md:col-span-5 space-y-4">
                      {selectedDriveFile ? (
                        <div className="bg-indigo-50/30 border border-indigo-100 rounded-xl p-5 space-y-4">
                          <h4 className="font-bold text-indigo-950 text-sm">Google Picker Selected File</h4>
                          <div className="space-y-3 text-xs text-indigo-900">
                            <p className="truncate"><span className="font-semibold text-indigo-950">Name:</span> {selectedDriveFile.name}</p>
                            <p className="truncate"><span className="font-semibold text-indigo-950">File ID:</span> <code className="bg-white px-1.5 py-0.5 rounded border border-indigo-100">{selectedDriveFile.id}</code></p>
                            <p className="truncate"><span className="font-semibold text-indigo-950">Mime-type:</span> {selectedDriveFile.mimeType}</p>
                            <p><span className="font-semibold text-indigo-950">Size:</span> {selectedDriveFile.size ? `${(parseInt(selectedDriveFile.size) / 1024).toFixed(1)} KB` : 'Folder / Custom'}</p>
                            
                            {selectedDriveFile.webViewLink && (
                              <a
                                href={selectedDriveFile.webViewLink}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-indigo-700 font-bold hover:underline"
                              >
                                View File on Drive <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="pt-2 flex gap-2">
                            <button
                              onClick={() => {
                                if (addToast) {
                                  addToast({
                                    title: 'Drive Attachment Picked',
                                    message: `File "${selectedDriveFile.name}" was successfully attached to the active procurement draft.`,
                                    type: 'success'
                                  });
                                }
                              }}
                              className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs cursor-pointer transition-colors"
                            >
                              Attach as Solicitation Doc
                            </button>
                            <button
                              onClick={() => setSelectedDriveFile(null)}
                              className="px-3 py-1.5 border border-indigo-200 text-indigo-700 font-semibold rounded-lg text-xs hover:bg-indigo-50 transition-colors"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 text-center text-xs text-gray-500 py-10">
                          Select a file in the Google Drive Explorer to use the Google Picker capabilities.
                        </div>
                      )}

                      {/* Upload text content */}
                      <form onSubmit={handleUploadFile} className="bg-gray-50/50 border border-gray-100 rounded-xl p-4 space-y-3.5">
                        <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-indigo-600">Quick-Upload text document</h4>
                        <div>
                          <input
                            type="text"
                            required
                            placeholder="File Name (e.g., proposal-appendix)..."
                            value={uploadName}
                            onChange={(e) => setUploadName(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>
                        <div>
                          <textarea
                            rows={3}
                            required
                            placeholder="Type file body text content..."
                            value={uploadContent}
                            onChange={(e) => setUploadContent(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-mono"
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          Upload to Drive <Plus className="w-4 h-4" />
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* === TAB CONTENT: TASKS === */}
              {activeTab === 'tasks' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <CheckSquare className="w-5 h-5 text-emerald-600" /> Google Tasks Checklist
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Organize response task milestones, compliance assignments, and sync due dates with Google Tasks.
                      </p>
                    </div>
                    <button
                      onClick={fetchTabData}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reload Tasks
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left: Task Creation Form */}
                    <form onSubmit={handleCreateTask} className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 space-y-4">
                      <h4 className="font-bold text-gray-900 text-sm">Add New Google Task</h4>
                      
                      <div className="space-y-3.5">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Link tracked solicitation</label>
                          <select
                            value={selectedSolicitationForTask}
                            onChange={(e) => {
                              setSelectedSolicitationForTask(e.target.value);
                              handlePreFillTaskFromSolicitation(e.target.value);
                            }}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          >
                            <option value="">-- Select solicitation --</option>
                            {savedList.map((sol) => (
                              <option key={sol.id || sol.solicitationId} value={sol.id || sol.solicitationId}>
                                [{sol.solicitationId}] {sol.title.slice(0, 45)}...
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Task Title</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g., Submit compliance form..."
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Notes / Reminders</label>
                          <textarea
                            rows={3}
                            placeholder="Add task notes, hyperlinks, or compliance details..."
                            value={newTaskNotes}
                            onChange={(e) => setNewTaskNotes(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Add Google Task
                        </button>
                      </div>
                    </form>

                    {/* Right: Active Tasks List */}
                    <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 flex flex-col h-[350px]">
                      <h4 className="font-bold text-gray-900 text-sm mb-4">Active Google Tasks</h4>
                      <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {tasks.length === 0 ? (
                          <div className="text-center py-20 text-gray-400 text-xs">
                            No active tasks found in default list.
                          </div>
                        ) : (
                          tasks.map((task) => (
                            <div 
                              key={task.id} 
                              className={`p-3 bg-white rounded-lg border flex gap-3 items-start transition-all ${
                                task.status === 'completed' ? 'border-gray-150 bg-gray-50/70' : 'border-gray-200 shadow-xs'
                              }`}
                            >
                              <button
                                onClick={() => handleToggleTask(task.id, task.status)}
                                className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center transition-colors shrink-0 cursor-pointer ${
                                  task.status === 'completed'
                                    ? 'bg-emerald-500 border-emerald-600 text-white'
                                    : 'border-gray-300 hover:border-indigo-500 bg-white'
                                }`}
                              >
                                {task.status === 'completed' && <Check className="w-3 h-3" />}
                              </button>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs font-bold text-gray-900 truncate ${task.status === 'completed' && 'line-through text-gray-400'}`}>
                                  {task.title}
                                </p>
                                {task.notes && (
                                  <p className={`text-[10px] text-gray-500 mt-1 whitespace-pre-wrap leading-normal ${task.status === 'completed' && 'text-gray-350'}`}>
                                    {task.notes}
                                  </p>
                                )}
                                {task.due && (
                                  <p className="text-[9px] font-semibold text-rose-500 mt-1.5">
                                    Due Date: {new Date(task.due).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* === TAB CONTENT: GMAIL & CONTACTS === */}
              {activeTab === 'gmail' && (
                <div className="space-y-6 animate-fade-in">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-rose-500" /> Gmail Dispatch & Google Contacts
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Compose collaboration messages, send draft response documents, or reach out to subcontractors via Gmail.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Contacts panel */}
                    <div className="md:col-span-5 bg-gray-50/50 border border-gray-100 rounded-xl p-4 flex flex-col h-[380px]">
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider text-indigo-600 mb-3">Google Contacts Link</h4>
                      <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {contacts.length === 0 ? (
                          <div className="text-center py-20 text-gray-400 text-xs">
                            No Google contacts found with active email addresses.
                          </div>
                        ) : (
                          contacts.map((contact) => (
                            <button
                              key={contact.resourceName}
                              onClick={() => handleSelectContact(contact.resourceName)}
                              className={`w-full text-left p-3 rounded-lg border text-xs transition-all flex gap-3 items-center cursor-pointer ${
                                selectedContact === contact.resourceName
                                  ? 'border-indigo-600 bg-indigo-50/50'
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold font-mono text-xs shrink-0">
                                {contact.name.slice(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-gray-950 truncate">{contact.name}</p>
                                <p className="text-[10px] text-gray-500 truncate mt-0.5">{contact.email}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Compose panel */}
                    <form onSubmit={handleSendEmail} className="md:col-span-7 bg-gray-50/50 border border-gray-100 rounded-xl p-5 space-y-4">
                      <h4 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                        <Send className="w-4 h-4 text-rose-500 animate-pulse" /> Compose Email (via Gmail)
                      </h4>

                      <div className="space-y-3.5">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">To Email</label>
                          <input
                            type="email"
                            required
                            placeholder="recipient@example.com..."
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Subject</label>
                          <input
                            type="text"
                            required
                            placeholder="Email subject..."
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Body Text</label>
                          <textarea
                            rows={5}
                            required
                            placeholder="Write your email proposal or response inquiry..."
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <Send className="w-3.5 h-3.5" /> Send Email via Gmail
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* === TAB CONTENT: CALENDAR === */}
              {activeTab === 'calendar' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5 text-blue-600" /> Google Calendar Deadlines Sync
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Map procurement submission timers and bid milestones directly to your live Google Calendar.
                      </p>
                    </div>
                    <button
                      onClick={fetchTabData}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reload Calendar
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left: Event scheduler */}
                    <form onSubmit={handleCreateEvent} className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 space-y-4">
                      <h4 className="font-bold text-gray-900 text-sm">Schedule Calendar Event</h4>

                      <div className="space-y-3.5">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Link tracked Solicitation</label>
                          <select
                            value={selectedSolicitationForCal}
                            onChange={(e) => {
                              setSelectedSolicitationForCal(e.target.value);
                              handlePreFillCalFromSolicitation(e.target.value);
                            }}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          >
                            <option value="">-- Select solicitation --</option>
                            {savedList.map((sol) => (
                              <option key={sol.id || sol.solicitationId} value={sol.id || sol.solicitationId}>
                                [{sol.solicitationId}] {sol.title.slice(0, 45)}...
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Event Summary</label>
                          <input
                            type="text"
                            required
                            placeholder="Event Title..."
                            value={eventSummary}
                            onChange={(e) => setEventSummary(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Description</label>
                          <textarea
                            rows={3}
                            placeholder="Add meeting links, compliance guidelines, or sub-tasks..."
                            value={eventDescription}
                            onChange={(e) => setEventDescription(e.target.value)}
                            className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Start Time</label>
                            <input
                              type="datetime-local"
                              required
                              value={eventDate}
                              onChange={(e) => setEventDate(e.target.value)}
                              className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Duration (Min)</label>
                            <select
                              value={eventDuration}
                              onChange={(e) => setEventDuration(e.target.value)}
                              className="w-full text-xs px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700"
                            >
                              <option value="15">15 Minutes</option>
                              <option value="30">30 Minutes</option>
                              <option value="60">1 Hour</option>
                              <option value="120">2 Hours</option>
                              <option value="240">4 Hours</option>
                            </select>
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Add to Google Calendar
                        </button>
                      </div>
                    </form>

                    {/* Right: Upcoming calendar events */}
                    <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 flex flex-col h-[380px]">
                      <h4 className="font-bold text-gray-900 text-sm mb-4">Upcoming Calendar Events</h4>
                      <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                        {calendarEvents.length === 0 ? (
                          <div className="text-center py-20 text-gray-400 text-xs">
                            No upcoming scheduled events found.
                          </div>
                        ) : (
                          calendarEvents.map((event) => (
                            <div key={event.id} className="p-3 bg-white rounded-lg border border-gray-200 shadow-xs flex justify-between items-start text-xs">
                              <div className="min-w-0 flex-1 pr-3">
                                <p className="font-bold text-gray-950 truncate">{event.summary}</p>
                                {event.description && (
                                  <p className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap leading-relaxed truncate max-h-12">
                                    {event.description}
                                  </p>
                                )}
                                <p className="text-[9px] font-semibold text-indigo-600 mt-2">
                                  {new Date(event.start.dateTime).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </p>
                              </div>
                              {event.htmlLink && (
                                <a
                                  href={event.htmlLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="p-1 border border-gray-200 rounded text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Bottom Status bar */}
            <div className="mt-8 pt-4 border-t border-gray-150 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-gray-400 shrink-0">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Active memory session: Google OAuth Verified
              </span>
              <span>Project Target ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono">apexcite-3aece</code></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
